import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FrownOutlined } from "@ant-design/icons";
import moment from "moment";

import LoadingContainer from "components/loading_container";
import Placeholder from "components/placeholder";

import { BookingActionsContext, BookingDataContext } from "containers/data_context";

import ReloadContainer from "./reload_container";
import RoomType from "./room_type";
import SectionTitle from "components/section_title/section_title";
import styles from "./rates_table.module.css";
import { NOT_BREAKFAST_ENTERIES } from "constants/defaults";
import useHandlePromotions from "hooks/useHandlePromotions";

const DEFAULT_OCCUPANCY_PER_ROOM = {};

export default function RatesTable({
  property = {},
  bookedRooms,
  update,
  disable,
  bookedRatesOccupancy = DEFAULT_OCCUPANCY_PER_ROOM,
  setShowLoginModal,
  isMemberShipEnabled,
}) {
  const [isStale, setIsStale] = useState(false);
  const { t } = useTranslation();
  const { roomsInfo, params, roomRequestParams, channelId } = useContext(BookingDataContext);
  const { setParams, loadRoomsInfo } = useContext(BookingActionsContext);

  const prevParamsRef = useRef(params);
  useEffect(() => {
    prevParamsRef.current = params;
  });
  const prevParams = prevParamsRef.current;
  const {
    ratesOccupancyPerRoom = DEFAULT_OCCUPANCY_PER_ROOM,
    currency,
    checkinDate = null,
    checkoutDate = null,
    adults,
    children,
  } = params;

  const { getRoomPromotions } = useHandlePromotions();

  const { data: loadedRoomsData, isLoading } = roomsInfo;
  const isReloadDisabled = !moment(checkinDate).isValid() || !moment(checkoutDate).isValid();
  const roomsData = bookedRooms?.length ? bookedRooms : loadedRoomsData;

  const setRatesOccupancyPerRoom = useCallback(
    (updatedOccupancy) => {
      setParams({ ...params, ratesOccupancyPerRoom: updatedOccupancy });
    },
    [params, setParams],
  );

  const handleReload = useCallback(() => {
    loadRoomsInfo(channelId, params);
  }, [channelId, params, loadRoomsInfo]);

  useEffect(
    function handleSearchParamsChange() {
      const isCheckinDateChanged = params.checkinDate !== prevParams.checkinDate;
      const isCheckoutDateChanged = params.checkoutDate !== prevParams.checkoutDate;
      const isDatesChaged = isCheckinDateChanged || isCheckoutDateChanged;

      const isCheckinDateMatchesLast = moment(roomRequestParams.checkinDate).isSame(
        params.checkinDate,
        "day",
      );
      const isCheckoutDateMatchesLast = moment(roomRequestParams.checkoutDate).isSame(
        params.checkoutDate,
        "day",
      );
      const isDatesMatchLastRequest = isCheckinDateMatchesLast && isCheckoutDateMatchesLast;

      if (isStale && isDatesMatchLastRequest) {
        setIsStale(false);
        return;
      }

      if (isDatesChaged) {
        setIsStale(true);
        setParams({ ...params, ratesOccupancyPerRoom: {} });
      }
    },
    [isStale, params, prevParams, roomRequestParams, setParams],
  );

  useEffect(
    function handleRoomsInfoUpdate() {
      setIsStale(false);
    },

    [roomsInfo],
  );

  if (!Array.isArray(roomsData)) {
    return null;
  }
  const showRatePlanIncluded = (rates) => {
    let showRatePlan = false;

    for (let i = 0; i < rates?.length; i++) {
      if (!NOT_BREAKFAST_ENTERIES?.includes(rates[i]?.mealType)) {
        showRatePlan = true;
        break;
      }
    }
    return showRatePlan;
  };

  return (
    <LoadingContainer loading={isLoading}>
      <ReloadContainer disabled={isReloadDisabled} active={isStale} onRefresh={handleReload}>
        <div>
          {roomsData?.length > 0 ? (
            <div className={styles.tableContainer}>
              <SectionTitle>{t("rates_table:title")}</SectionTitle>
              {roomsData.map((roomType, rowIndex) => (
                <RoomType
                  isMemberShipEnabled={isMemberShipEnabled}
                  setShowLoginModal={setShowLoginModal}
                  disabled={isStale}
                  roomType={roomType}
                  showBreakFastIncluded={showRatePlanIncluded(roomType?.ratePlans)}
                  currency={currency}
                  rowIndex={rowIndex}
                  update={update}
                  disable={disable}
                  promotions={getRoomPromotions(roomType?.id)}
                  adultsOccupancy={adults}
                  childrenOccupancy={children}
                  key={roomType.id}
                  property={property}
                  bookedRatesOccupancy={bookedRatesOccupancy}
                  ratesOccupancyPerRoom={ratesOccupancyPerRoom}
                  onRatesOccupancyChange={setRatesOccupancyPerRoom}
                />
              ))}
            </div>
          ) : isLoading ? (
            <div className="mt-5"></div>
          ) : (
            <div className="mt-5">
              <Placeholder
                phone={property?.phone}
                email={property?.email}
                icon={<FrownOutlined />}
                text={t("rates_table:no_available_rates_placeholder")}
              />
            </div>
          )}
        </div>
      </ReloadContainer>
    </LoadingContainer>
  );
}
